import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Mousetrap from 'mousetrap';
import { isValidNumber, getCountries, getPhoneCode, formatIncompletePhoneNumber } from 'libphonenumber-js';
import './CreatorApplicationPersonalInfo.scss';

import { doesUserExist as doesUserExistAPI } from '../../APIClient/users';
import { updateCreatorApplication } from '../../Actions/ApplicationActions';
import { isValidEmail } from '../../Helpers/formatting';
import { isShopper, getProfileFromObject } from '../../Helpers/user_helpers';

import ScrollToTop from '../../Components/General/ScrollToTop';
import Tooltip from '../../Components/General/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

const CreatorApplicationPersonalInfo = props => {
  const { user, referringUser, referringBrand, removeReferral } = props;
  const application = props.application.creatorApplication;

  const [isLoading, setIsLoading] = useState(false);
  const [emailExists, setEmailExists] = useState(false);

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [fields, setFields] = useState([
    {
      name: 'firstName',
      label: 'First Name*',
      value: application.firstName || '',
      isFieldValid: value => value?.length >= 2,
      isValid: true,
      errorMessage: 'First name must be at least 2 characters long',
      canEdit: !isShopper(user)
    },
    {
      name: 'lastName',
      label: 'Last Name*',
      value: application.lastName || '',
      isFieldValid: value => value?.length >= 2,
      isValid: true,
      errorMessage: 'Last name must be at least 2 characters long',
      canEdit: !isShopper(user)
    },
    {
      name: 'email',
      label: 'Email*',
      value: application.email || '',
      isFieldValid: value => isValidEmail(value),
      isValid: true,
      errorMessage: 'Please enter a valid email',
      canEdit: !isShopper(user)
    },
    {
      name: 'phone',
      label: 'Phone Number',
      value: application.phone || '',
      phoneExtension: application.phoneExtension || '1',
      canEdit: true,
      isFieldValid: (value, field) => {
        const fullValue = `+${field.phoneExtension}${value}`;
        const validity = isValidNumber(fullValue);
        return validity;
      },
      isValid: true,
      errorMessage: 'Please enter a valid phone number',
      customComponent: (field, index) => {
        return (
          <div className='creator-application-input-container' key={index}>
            <select
              className='creator-application-phone-country-select'
              id='countryCode'
              onChange={e => {
                const { value } = e.target;
                setFields(prev => updateField(prev, index, { phoneExtension: value }));
                props.updateCreatorApplication({ phoneExtension: value });
              }}
              value={field.phoneExtension || ''}
            >
              <option value=''>Ext.</option>
              <option value='1'>US (+1)</option>

              {getCountries().map(country => (
                <option key={country} value={getPhoneCode(country)}>
                  {country} (+{getPhoneCode(country)})
                </option>
              ))}
            </select>

            <input
              className={cn('creator-application-input spacing-left', {
                invalid: !field.isValid || (field.name === 'email' && emailExists),
                filled: !!field.value
              })} // With this we can delay when we show to the errored state
              type='text'
              placeholder={field.label}
              id={field.name}
              value={field.value}
              onChange={e => {
                let { value } = e.target;
                const formattedValue = formatIncompletePhoneNumber(value, field.phoneExtension);
                setFields(prev => updateField(prev, index, { value: formattedValue }));
                props.updateCreatorApplication({ phone: formattedValue });
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  goToNextTab();
                }

                if (e.key === 'Escape') {
                  e.preventDefault();
                  const newFields = [...fields];
                  newFields[index].value = '';
                  setFields(newFields);
                  props.updateCreatorApplication({ [field.name]: '' });
                }
              }}
            />
          </div>
        );
      }
    }
  ]);

  const initializeApplicationForShopper = () => {
    if (!isShopper(user) && getProfileFromObject(user)?.id) return window.ALERT.warn('You cannot apply as you already have a creator account.');
    else if (!isShopper(user)) return;

    const { id: ShopperUser_id, name, email } = getProfileFromObject(user);
    const [firstName, ...splitLastName] = name.split(' ');
    const lastName = splitLastName.join(' ');

    const newFields = fields.map(field => {
      if (field.name === 'firstName') return { ...field, value: firstName, isValid: true };
      if (field.name === 'lastName') return { ...field, value: lastName, isValid: true };
      if (field.name === 'email') return { ...field, value: email, isValid: true };
      return { ...field };
    });

    setFields(newFields);
    props.updateCreatorApplication({ firstName, lastName, email, ShopperUser_id });
  };

  const validateFields = async () => {
    let firstErrorMessage = null;
    setIsLoading(true);

    let exists = false;
    if (!isShopper(user)) {
      const email = fields.find(field => field.name === 'email').value;
      const emailExistsResponse = email && (await doesUserExistAPI({ email }));
      exists = emailExistsResponse?.exists;
      setEmailExists(exists);
    }

    const newFields = fields.map(field => {
      let isValidField = field.canEdit ? field.isFieldValid(field.value, field) : true;
      if (field.name === 'email' && exists) {
        isValidField = false;
        firstErrorMessage = 'This email is already in use';
      }

      if (!isValidField) firstErrorMessage = firstErrorMessage || field.errorMessage;
      return { ...field, isValid: isValidField };
    });

    setFields(newFields);
    setIsLoading(false);
    return firstErrorMessage;
  };

  const goToNextTab = async () => {
    const errorMessage = await validateFields();
    if (errorMessage) window.ALERT.error(errorMessage);
    else props.goToNextTab();
  };

  useEffect(() => {
    initializeApplicationForShopper();

    Mousetrap.bind('enter', e => {
      e.preventDefault();
      goToNextTab();
    });

    return () => {
      Mousetrap.unbind('enter');
    };
  }, []);

  useEffect(() => {
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (field.isValid) continue;

      const isValidField = field.isFieldValid(field.value, field);
      if (isValidField) {
        setFields(fields => {
          const newFields = [...fields];
          newFields[i].isValid = true;
          return newFields;
        });
      }
    }
  }, [fields]);

  useEffect(() => {
    if (!referringUser?.id && !referringBrand?.id) return;

    if (referringUser) props.updateCreatorApplication({ ReferringUser_id: referringUser.id, ReferringBrand_id: null, referringUser });
    else props.updateCreatorApplication({ ReferringUser_id: null, ReferringBrand_id: referringBrand.id, referringBrand });
  }, [referringUser, referringBrand]);

  return (
    <div className='creator-application-personal-info-outer'>
      <ScrollToTop />
      <div className='creator-application-personal-info-inner'>
        <div className='creator-application-progress-container'>
          <div className='creator-application-progress-container-circle active'>1</div>
          <div className='creator-application-progress-container-circle clickable' onClick={goToNextTab}>
            2
          </div>
          <div className='creator-application-progress-container-circle'>3</div>
        </div>

        <h1>Apply to be a ShopMy Creator</h1>
        <h2>Monetize your product recommendations and build meaningful relationships with the world’s best brands.</h2>

        <div className='creator-application-personal-info-form'>
          {fields.map((field, index) => (
            <div key={index} className='creator-application-input-container'>
              <Tooltip message={field.name === 'email' && emailExists ? 'This email is already in use' : field.isValid ? '' : field.errorMessage}>
                {field.customComponent ? (
                  field.customComponent(field, index)
                ) : (
                  <input
                    className={cn('creator-application-input', {
                      invalid: !field.isValid || (field.name === 'email' && emailExists),
                      filled: !!field.value
                    })} // With this we can delay when we show to the errored state
                    type='text'
                    placeholder={field.label}
                    id={field.name}
                    value={field.value}
                    onChange={e => {
                      const { value } = e.target;
                      setFields(prev => updateField(prev, index, { value }));
                      props.updateCreatorApplication({ [field.name]: value });
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        goToNextTab();
                      }

                      if (e.key === 'Escape') {
                        e.preventDefault();
                        const newFields = [...fields];
                        newFields[index].value = '';
                        setFields(newFields);
                        props.updateCreatorApplication({ [field.name]: '' });
                      }
                    }}
                    disabled={!field.canEdit}
                  />
                )}
              </Tooltip>

              {!!field.supportText && (
                <div className={cn('creator-application-input-support-text', { alignRight: !field.inputValueAlignRight })}>{field.supportText}</div>
              )}
            </div>
          ))}

          {!!(referringUser || referringBrand) && (
            <div className='creator-application-referring-user' onClick={() => removeReferral()}>
              <p>Referral: {referringUser?.name || referringBrand.name}</p>
              <FontAwesomeIcon icon={faTimes} className='creator-application-referring-user-close' />
            </div>
          )}

          <div className='creator-application-progress-button-container'>
            <button className='creator-application-progress-button' onClick={() => props.goToPreviousTab()}>
              {!!(referringUser || referringBrand) ? 'Back' : 'Go To Home'}
            </button>
            <button className={cn('creator-application-progress-button primary', { isLoading })} onClick={goToNextTab}>
              Next
            </button>
          </div>

          <a href='https://shopmy.us/home/brands' className='creator-application-progress-link'>
            I am a Brand
          </a>
        </div>
      </div>
    </div>
  );
};

CreatorApplicationPersonalInfo.propTypes = {
  application: PropTypes.object.isRequired,
  updateCreatorApplication: PropTypes.func.isRequired,
  goToNextTab: PropTypes.func.isRequired,
  goToPreviousTab: PropTypes.func.isRequired,

  referringUser: PropTypes.object,
  referringBrand: PropTypes.object,
  removeReferral: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  user: state.user,
  application: state.application
});

export default connect(mapStateToProps, {
  updateCreatorApplication
})(CreatorApplicationPersonalInfo);
